import React from "react";
import styles from "./HabiTrackLegal.module.scss";
import { Icon } from "../../assets/icons/Icon";

const HabiTrackLegal: React.FC = () => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className={styles['ht-legal']}>
        <div className={styles['ht-legal__logo']}>
          <Icon iconName="Logo" width="8rem" height="1.88rem" />
          </div>
      <h1 className={styles['ht-legal__title']}>HabiTrack™</h1>
      <p className={styles['ht-legal__meta']}>Version 1.0 — October 2025</p>

      {/* ==================== TERMS OF SERVICE ==================== */}
      <section className={styles['ht-section']}>
        <h2 className={styles['ht-section__title']}>User Terms of Service</h2>

        <div className={styles['ht-section__toc']}>
          <h3>Contents</h3>
          <ol>
            <li>
              <button 
                onClick={() => scrollToSection('terms-introduction')}
                className={styles['ht-section__toc-link']}
              >
                Introduction
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('terms-use')}
                className={styles['ht-section__toc-link']}
              >
                Use of HabiTrack™
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('terms-products')}
                className={styles['ht-section__toc-link']}
              >
                Our Products and Services
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('terms-links')}
                className={styles['ht-section__toc-link']}
              >
                Links to Third Party Content
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('terms-termination')}
                className={styles['ht-section__toc-link']}
              >
                Termination and Cancellation
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('terms-disclaimers')}
                className={styles['ht-section__toc-link']}
              >
                Disclaimers
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('terms-indemnity')}
                className={styles['ht-section__toc-link']}
              >
                Indemnity
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('terms-contact')}
                className={styles['ht-section__toc-link']}
              >
                Contact
              </button>
            </li>
          </ol>
        </div>

        <h3 id="terms-introduction">1. Introduction</h3>
        <p>
          The following are the terms and conditions ("Terms") under which you, an individual user ("User"), may use the online platform HabiTrack ("Platform"). It is important that you also read the HabiTrack User Privacy Policy. By accessing the Platform, you acknowledge and agree that you have read, understand, and agree to be bound by the Terms and the Privacy Policy. This includes access by any method (e.g. application or web-browser) or from any device.
        </p>
        <p>
          FabricShift Ltd. ("FS") may revise these Terms at any time by updating them as they are displayed within the Platform. Whenever this happens, you will be informed when you next log in to the Platform. The version number and date of the latest update will always be displayed at the top of this Terms document.
        </p>

        <h3 id="terms-use">2. Use of HabiTrack™</h3>
        <p>
          The contents of the Platform are protected by international copyright, trademark, patent, and other intellectual property laws. All Platform content is exclusive property of FS, its content suppliers, or clients.
        </p>
        <p>
          FS processes personal data on behalf of your employer, who is the data controller for your information (see Privacy Policy for details). FS retains ownership of all Platform content and intellectual property. Your employer is responsible for how your personal data is collected and used within the Platform.
        </p>
        <p>
          However, should you wish to edit this profile within the Platform, by providing User content, you accept responsibility for the content that you post. You also accept responsibility for any content you upload throughout the Platform. FS and your employer reserve the right to remove any content from the Platform at any time.
        </p>
        <p>
          Unauthorised copying or use of the Platform may violate intellectual property rights or other laws. You may not use, display, reproduce, modify, transmit, sell, or distribute any of the Platform content by any method whatsoever. You are not to use the Platform in violation of any applicable laws or regulations or in a manner that that will infringe FS's, or any third party's, intellectual property rights. You must not do anything that could bring FS into disrepute or which could violate the rights of any person.
        </p>
        <p>
          FS prohibits use of the Platform by any User who is not using the Platform as described within these Terms or within the Platform. This includes: using any device or software to interfere with Platform operation; using any mechanism to navigate the Platform other than features made available in the Platform; copying and/or distributing any content from within the Platform; inflict or attempts to inflict an unreasonable load on the Platform's infrastructure, including (but not limited to) denial of service attacks; and attempting to deconstruct or reconstruct any part of the Platform whatsoever.
        </p>

        <h3 id="terms-products">3. Our Products and Services</h3>
        <p>
          The Platform, and all content, features, products and services contained within, are subject to these Terms, the Privacy Policy, and an agreement between FS and your employer.
        </p>
        <p>
          The Platform is a digital habit-tracking service, designed to support behaviour change and personal development. It allows users to log habits, track progress, receive nudges and feedback, take part in leaderboards, and view insights to support ongoing improvement. To use the products and services, you agree not to sign up on behalf of someone else and that you are not less than 18 years of age.
        </p>
        <p>
          During the course of using HabiTrack, you may be asked to log habits, track progress, respond to short "pulse" questions (e.g., daily mood), and participate in leaderboards or other gamified features. You agree to partake in these activities honestly with no malicious intention of manipulating the aggregate result whatsoever.
        </p>
        <p>
          During the course of some learning and development programs, you will be asked to use HabiTrack to log habits and record your task progress. By doing so, you agree to submit this information honestly and not to mendaciously submit information repetitively and/or excessively.
        </p>
        <p>
          Occasionally, you will be asked to complete "pulse questions" (e.g., daily mood checker, or short reflections), which are designed to help track your progress and wellbeing. As with all Platform content and functionality, you agree to answer these questions without sharing or distributing the questions or results with anybody else.
        </p>
        <p>
          Over time, we will add new and edit existing products and services within the Platform. You are permitted to use these products and services only as described in the Platform and set out in these Terms.
        </p>

        <h3 id="terms-links">4. Links to Third Party Content</h3>
        <p>
          The Platform currently operates within Microsoft Teams and may connect to other third-party systems (such as HR or learning-management platforms) to support your employer's use of HabiTrack. Your use of those third-party services is also governed by their own terms and privacy policies (e.g., Microsoft's Terms of Use and Privacy Statement for Teams).
        </p>
        <p>
          FS takes no responsibility for the content, security, or data-handling practices of any third-party services. We encourage users to familiarise themselves with the relevant third-party terms before using these features. Future integrations may be added, and this section will continue to apply to all such integrations.
        </p>

        <h3 id="terms-termination">5. Termination and Cancellation</h3>
        <p>
          FS and your employer reserve the right to terminate or suspend your access to the Platform, delete your account, remove your profile information, and/or remove any material you have uploaded for any reason whatsoever. In particular, we will make every effort to do this if we find that you are in violation of these Terms or the Privacy Policy.
        </p>
        <p>
          As your account is created under an agreement between FS and your employer, you do not have the right to delete your account. If you wish for your account to be deleted and/or your personal information to be removed, you must contact your employer. However, FS will be happy to handle any questions regarding issues with your account. Please contact info@fabricshift.com.
        </p>
        <p>
          Your access to the Platform will be terminated at the end of our contract with your employer. There will be a "cooling off period" while we retain your personal data after the contract ends. After this cooling off period, your Platform profile and personal data will be permanently deleted.
        </p>
        <p>
          When we delete your account and terminate your and your employer's access to the Platform, we reserve the right to anonymise any data stored on the Platform before permanently deleting it. We keep anonymous data for research purposes, in order to better understand how users interact with the Platform, and to improve future products and services. This is discussed in our Privacy Policy.
        </p>

        <h3 id="terms-disclaimers">6. Disclaimers</h3>
        <p>
          THE PLATFORM AND ALL THE CONTENTS WITHIN ARE PROVIDED ON AN "AS IS" BASIS WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESSED OR IMPLIED.
        </p>
        <p>
          FABRICSHIFT MAKES NO WARRANTIES ABOUT THE ACCURACY, RELIABILITY, COMPLETENESS, QUALITY OR TIMELINESS OF ANY SERVICES OR CONTENT ACCESSIBLE THROUGH THE PLATFORM. FABRICSHIFT MAKES NO WARRANTIES ABOUT THE OPERATABILITY OF THE PLATFORM, INCLUDING BUT NOT LIMITED TO SERVER DOWNTIME, INABILITY TO ACCESS, SYSTEM FAILURES, AND OTHER ERRORS.
        </p>
        <p>
          YOU ARE RESPONSIBLE FOR ALL MATERIAL YOU UPLOAD TO THE PLATFORM AND UNDERSTAND THAT IN SOME CASES THIS CONTENT WILL BE DISCLOSED WITH YOUR EMPLOYER. YOU ARE RESPONSIBLE FOR ALL COMMUNICATIONS WITH OTHER USERS OF THE PLATFORM. FABRICSHIFT MAKES NO WARRANTIES FOR THE ACTIONS AND COMMUNICATIONS OF ANY CURRENT AND FUTURE USERS OF THE PLATFORM. YOU AGREE TO TAKE REASONABLE PRECAUTIONS IN ALL COMMUNICATIONS WITH OTHERS ON THE SITE AND UNDERSTAND THAT YOU SHOULD NOT SHARE PRIVATE OR SENSITIVE INFORMATION.
        </p>
        <p>
          THE PROVISION OF PRODUCTS AND SERVICES THROUGH THE PLATFORM TO YOU IS CONTINGENT ON THE AGREEMENT BETWEEN FABRICSHIFT AND YOUR EMPLOYER. ANY LEGAL ISSUES YOU TAKE WITH THESE TERMS OR THE PRIVACY POLICY SHOULD BE DIRECTED TO YOUR EMPLOYER, WHO RETAINS RESPONSIBILITY FOR HANDLING ANY REQUESTS.
        </p>
        <p>
          IN NO EVENT SHALL FABRICSHIFT BE LIABLE TO ANY USER OR ANY THIRD PARTY FOR LOST PROFITS, LOSS OF DATA, SERVICE INTERRUPTION, COMPUTER DAMAGE, SYSTEM FAILURE, PHYSICAL INJURY, EMOTIONAL DISTRESS, OR ANY DAMAGES WHATSOEVER RESULTING FROM USE OF THE PLATFORM. TO THE EXTENT THAT APPLICABLE LAW RESTRICTS THIS RELEASE OF LIABILITY, YOU AGREE THAT WE ARE ONLY LIABLE TO YOU FOR THE MINIMUM AMOUNT OF DAMAGES THAT THE LAW RESTRICTS OUR LIABILITY TO, IF SUCH A MINIMUM EXISTS.
        </p>

        <h3 id="terms-indemnity">7. Indemnity</h3>
        <p>
          You agree to defend, indemnify and hold harmless FS and all associated personnel from and against any third-party claims, actions, or demands, arising from your use of the Platform or your breach of these Terms.
        </p>

        <h3 id="terms-contact">8. Contact</h3>
        <p>
          For questions or concerns regarding these Terms, please contact us at:<br />
          Email: <a href="mailto:info@fabricshift.com">info@fabricshift.com</a><br />
          Address: 13 Hanover Square, London, W1S 1HN
        </p>
        <p>
          For unresolved concerns or legal issues, please contact your employer.
        </p>
      </section>

      {/* ==================== PRIVACY POLICY ==================== */}
      <section className={styles['ht-section']}>
        <h2 className={styles['ht-section__title']}>Privacy Policy</h2>

        <div className={styles['ht-section__toc']}>
          <h3>Contents</h3>
          <ol>
            <li>
              <button 
                onClick={() => scrollToSection('privacy-introduction')}
                className={styles['ht-section__toc-link']}
              >
                Introduction
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('privacy-products')}
                className={styles['ht-section__toc-link']}
              >
                Our Products & Services
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('privacy-data-collected')}
                className={styles['ht-section__toc-link']}
              >
                What Data is Collected?
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('privacy-data-why')}
                className={styles['ht-section__toc-link']}
              >
                Why is Data Collected?
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('privacy-data-how')}
                className={styles['ht-section__toc-link']}
              >
                How is Data Collected?
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('privacy-data-where')}
                className={styles['ht-section__toc-link']}
              >
                Where is Data Stored?
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('privacy-data-disclosure')}
                className={styles['ht-section__toc-link']}
              >
                Disclosures of Personal Data
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('privacy-data-retention')}
                className={styles['ht-section__toc-link']}
              >
                For How Long Does FS Store Personal Data?
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('privacy-data-anonymous')}
                className={styles['ht-section__toc-link']}
              >
                Anonymous Data
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('privacy-data-access')}
                className={styles['ht-section__toc-link']}
              >
                Who Has Access to the Data?
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('privacy-data-rights')}
                className={styles['ht-section__toc-link']}
              >
                Personal Data and Your Rights
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('privacy-cookies')}
                className={styles['ht-section__toc-link']}
              >
                Cookies
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('privacy-security')}
                className={styles['ht-section__toc-link']}
              >
                Data Security
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('privacy-contact')}
                className={styles['ht-section__toc-link']}
              >
                Contact Details
              </button>
            </li>
          </ol>
        </div>

        <h3 id="privacy-introduction">1. Introduction</h3>
        <p>
          This Privacy Policy is between FabricShift Ltd. ("FS") and you, the individual user ("User") of our online platform, HabiTrack™ ("Platform").
        </p>
        <p>
          FS respects your privacy and is committed to protecting your personal data. We define "personal data" according to the UK General Data Protection Regulation (UK GDPR) and, where applicable, the EU General Data Protection Regulation 2016/679 (EU GDPR) as information relating to natural persons who:
        </p>
        <ul>
          <li>can be identified or who are identifiable, directly from the information in question</li>
          <li>can be indirectly identified from that information in combination with other information.</li>
        </ul>
        <p>
          FS wants to help you reach your goals and build positive work-related habits while providing you the best possible experience. To do this, we need to understand how you use and interact with the Platform, so we can deliver a service that is personalised and relevant to you. This is done by collecting and processing personal data from you, as detailed below.
        </p>
        <p>
          Your privacy and the security of your personal data are of the utmost importance to us. This Privacy Policy explains how and why we gather, store, share and use your personal data.
        </p>
        <p>
          Occasionally, changes in law or the addition of new features mean we revise this Privacy Policy. Whenever this happens, you will be informed when you next log in to the Platform. The date of the latest update will always be displayed at the top of the Privacy Policy.
        </p>
        <p>
          Please be aware that we are not responsible for the privacy policies of other websites or applications to which links are included in the Platform.
        </p>

        <h3 id="privacy-products">2. Our Products & Services</h3>
        <p>
          This Privacy Policy refers to data collected strictly for FS products and services that are accessible via the HabiTrack™ Platform. While FS offers further products and services (separate to the Platform) that may require use of personal data, FS's practices and policies surrounding this data are governed by a business-to-business ("B2B") licence agreement between your employer and FS.
        </p>
        <p>
          Your use of products and services within the Platform is administered by your employer pursuant to the B2B licence agreement. According to this agreement, your employer is the controller of your personal data, while FS is the processor, and you (the employee) are a data subject. These definitions are set out in the GDPR as follows:
        </p>
        <ul>
          <li>"controller" means the natural or legal person, public authority, agency or other body which, alone or jointly with others, determines the purposes and means of the processing of personal data.</li>
          <li>"processor" means a natural or legal person, public authority, agency or other body which processes personal data on behalf of the controller.</li>
          <li>"data subject" means an identified or identifiable natural person.</li>
        </ul>
        <p>
          As processor of your personal data, FS is not responsible for our B2B customers' (including your employer's) policies and practices surrounding data privacy and security. While FS is also not responsible for responding to requests concerning the personal data we process on behalf of our B2B customers, we will endeavour to respond to any requests from data subjects in the interest of transparency.
        </p>
        <p>
          For any personal data where FS acts as an independent controller (for example, Platform diagnostics/telemetry or our public website), FS will handle that data in accordance with this Privacy Policy and applicable law. (This does not change your employer's role as controller for HabiTrack usage data.)
        </p>
        <p>
          Your employer retains full responsibility for responding to requests regarding personal data processed through the Platform. We encourage you to refer to your employer's privacy policy.
        </p>

        <h3 id="privacy-data-collected">3. What Data is Collected?</h3>
        <p>
          FS only stores personal data that is strictly necessary for operation of the HabiTrack™ Platform. We also collect data on how you interact with Platform functionality to optimise your experience and to provide reports to authorised administrators at your organisation. These reports are based on aggregated data, meaning no individuals are personally identifiable (except where your organisation enables features designed to show identifiable activity, such as endorsements or leaderboards).
        </p>
        <p>
          To set up your profile within the Platform, we store basic employee data from your employer's HR system(s). This includes information such as employee ID number, name, surname, corporate email, job title, department, region, and hours worked. (If your employer provides additional fields, those may also be processed as configured by your employer.)
        </p>
        <p>
          For the Platform to operate effectively, FS collects technical data related to the device and/or browser you are using (see the Cookies and Technical Data section below for more details).
        </p>
        <p>
          FS also stores data created as users interact with the Platform. This includes user activity within the Platform, such as any data, content, other materials that a user uploads, submits or otherwise transmits to or through the Platform, as well as habit check-ins, streaks, endorsements, and leaderboard participation.
        </p>
        <p>
          When analysing usage of the Platform, FS will process certain data for profiling. Profiling means that we process your personal data to analyse or predict aspects of your use of the Platform and your engagement with its features. The data we process includes user habits and actions taken by you—for example, when you take actions on the Platform, move between screens and press buttons. We do this profiling to monitor engagement with different features, to explore ways to make the Platform more effective, and to vary features or content to better match your usage patterns and preferences.
        </p>
        <p>
          If you have any questions on the type of Platform data we collect, please contact info@fabricshift.com.
        </p>

        <h3 id="privacy-data-why">4. Why is Data Collected?</h3>
        <p>
          We only collect personal data where we have a lawful basis to do so under the UK GDPR and, where applicable, the EU GDPR. Our purposes for collecting personal data are as follows:
        </p>
        <ul>
          <li>To fulfil our contract with your employer. This includes verifying your identity, communicating with you, and ensuring our products and services operate effectively within the Platform.</li>
          <li>To pursue our legitimate interests and enable our business. This includes monitoring Platform activity and performance, improving Platform functionality, personalising Platform content and features (e.g., habit nudges and leaderboards), investigating and/or reporting fraud or security incidents, and investigating complaints received from you or from others. (For certain limited purposes such as diagnostics/telemetry, FS may act as an independent controller—this does not change your employer's role as controller for HabiTrack usage data.)</li>
          <li>To comply with applicable laws and to protect FS's legitimate business interests and legal rights.</li>
          <li>With your consent (where required). For example, for non-essential cookies or analytics on the marketing website or in the Platform where consent is required by law.</li>
        </ul>
        <p>
          We do not use your personal data for third-party marketing purposes.
        </p>

        <h3 id="privacy-data-how">5. How is Data Collected?</h3>
        <p>
          There are two sources through which we collect data associated with users of the Platform:
        </p>
        <ul>
          <li>Data sourced from your employer's HR system(s). This data was provided by you to your employer and shared with FS to set up and manage your HabiTrack account (for example, name, corporate email, job title and team/region).</li>
          <li>Data generated as you use the Platform (including data you submit). We collect technical data about your device and/or browser when using the Platform (see the Cookies and Technical Data section). We also collect usage data about how you interact with the Platform (for example, habit check-ins and streaks, moods or pulse questions, endorsements and comments, and participation in leaderboards). In addition, we collect any content you choose to submit through the Platform and, where enabled by your organisation, metadata needed to operate integrations (e.g., with collaboration tools or HR/L&D systems).</li>
        </ul>

        <h3 id="privacy-data-where">6. Where is Data Stored?</h3>
        <p>
          FS stores personal data on Amazon Web Services (AWS). We store and process data in the UK. Your employer and FS agree that data may be held by AWS on behalf of FS; AWS acts as our sub-processor under a data processing agreement.
        </p>
        <p>
          Where personal data needs to be transferred between regions or outside the UK/EEA, this is done in accordance with the B2B licence agreement and in compliance with applicable transfer rules (e.g., UK International Data Transfer Addendum or EU Standard Contractual Clauses, plus transfer risk assessments, as required by law).
        </p>

        <h3 id="privacy-data-disclosure">7. Disclosures of Personal Data</h3>
        <p>
          It is important to note, FS does not act as the controller for personal data processed in relation to Platform users. FS acts as the processor on behalf of your employer, which means disclosures and international transfers are governed by your employer's instructions and the B2B licence/data processing agreement between your employer and FS. (For limited purposes such as service diagnostics/telemetry or our public website, FS may act as an independent controller—this does not change your employer's role as controller for HabiTrack usage data.)
        </p>
        <p>
          Our agreement with your employer means we will not disclose to them any personally identifiable confidential information that they do not already hold within their HR systems, except where the feature is designed to be visible (for example, your public profile, comments/endorsements, or leaderboard entries) or where your organisation has explicitly enabled visibility. To protect your privacy, reports provided to your employer are based on aggregated or de-identified data.
        </p>
        <p>
          By "confidential information" within the Platform, we mean data you provide in a non-public setting (for example, mood or pulse question responses, private goal tracking). Information you choose to post in public or shared spaces (such as endorsements or leaderboards) is not treated as confidential. While aggregated insights from surveys, evaluations or programme progress may be reported to your employer, the individual data points are not shared unless your organisation has enabled a feature that intentionally displays them, in which case you will be informed within that feature.
        </p>
        <p>
          The Platform relies on several microservices and third-party providers to operate and keep the Platform secure. For the purposes set out in this Privacy Policy, FS shares your personal data with these sub-processors, who process your information on behalf of FS and strictly for those purposes. We require appropriate contractual and technical safeguards (including confidentiality, security, and—where relevant—SCCs/UK IDTA for international transfers).
        </p>
        <p>
          In the event of a merger, divestiture, restructuring, reorganisation, dissolution, sale, or other transfer of some or all of our assets, we may transfer your personal data to the relevant transferee. We will use reasonable efforts to ensure the transferee uses your personal data consistently with this Privacy Policy.
        </p>
        <p>
          If required by law, or to protect our legitimate or legal interests, FS may disclose personal data to third parties such as governmental authorities or law enforcement. Where legally permitted, we will notify your employer (as controller) of such requests and handle them in accordance with our agreement with your employer.
        </p>

        <h3 id="privacy-data-retention">8. For How Long Does FS Store Personal Data?</h3>
        <p>
          FS operates a data retention process (agreed with your employer and compliant with applicable law) under which we delete or anonymise personal data whenever:
        </p>
        <ul>
          <li>The customer ends its relationship with FS or is no longer using the Platform.</li>
          <li>An individual is no longer authorised to use the Platform. For example, when an employee leaves the organisation or your employer asks us to remove the account. (Requests from individuals are handled by your employer as controller; FS supports your employer's instructions.)</li>
          <li>Our periodic reviews identify data that is no longer necessary for the purposes for which it was collected.</li>
        </ul>
        <p>
          Before we remove personal data from our systems, we may convert certain datasets into aggregated or anonymised form for internal analytics, research and product improvement. This information does not identify you.
        </p>
        <p>
          In some cases, we may retain limited information for a short period to comply with legal, regulatory or contractual obligations (e.g., records needed for security or audit). Personal data deleted from active systems may remain in backups for a limited time before being overwritten in line with our backup retention schedules.
        </p>

        <h3 id="privacy-data-anonymous">9. Anonymous Data</h3>
        <p>
          Any data we classify as "anonymous" is determined in accordance with Recital 26 of the GDPR (and the equivalent standard under the UK GDPR). Once anonymised, the information does not relate to an identified or identifiable natural person. We take steps to ensure that individuals are not reasonably likely to be re-identified, including where additional information may become available.
        </p>
        <p>
          FS anonymises data so that we can retain it for research purposes and to improve our products and services offered within the Platform. We take a number of steps to anonymise, including:
        </p>
        <ul>
          <li>Removing all personal identifiers.</li>
          <li>Replacing exact figures with ranges (e.g., ages become age groups).</li>
          <li>Aggregating data for teams smaller than 10 (or a higher threshold where appropriate).</li>
          <li>Aggregating data for teams that we assess could still be identifiable in some way (for example, where an executive team could be identified using other information such as company size or sector).</li>
        </ul>
        <p>
          Anonymised data is no longer personal data. FS may retain anonymised datasets to analyse usage trends and improve HabiTrack without identifying any individual.
        </p>

        <h3 id="privacy-data-access">10. Who Has Access to the Data?</h3>
        <p>
          As your employer is the controller of the data, rights to access are governed by your employer–employee agreement. The exception is data you provide to FS in a non-public setting (for example, Evaluation Module responses, private habit logs, or personal notes). Your employer will only access this information in aggregated or de-identified form. Information you choose to share in public or shared features (such as your profile, endorsements/comments, or leaderboards) may be visible to others in your organisation by design.
        </p>
        <p>
          For internal access within FS, data access is segregated between the FS technology team and the FS data team. The technology team is responsible for Platform operation. The data team is responsible for customer reporting and analytics.
        </p>
        <p>
          Once the data required for Platform operation has been transferred into HabiTrack, day-to-day operational access sits with the technology team. Access within FS is restricted on a need-to-know basis.
        </p>

        <h3 id="privacy-data-rights">11. Personal Data and Your Rights</h3>
        <p>
          As FS is the processor of your personal data, your employer (the data controller) retains legal responsibility when you choose to exercise your rights under data protection laws. The following requests should be directed to your employer:
        </p>
        <ul>
          <li>Data Subject Access Request (DSAR) – the right to request a copy of your personal data and to check it is being processed lawfully.</li>
          <li>Rectification requests – to correct personal data you believe to be inaccurate or incomplete.</li>
          <li>Erasure requests – to request deletion of your personal data, which would result in you no longer being able to access the Platform.</li>
        </ul>
        <p>
          You may also have rights to restrict processing, data portability, object to certain processing (for example, where based on legitimate interests), and to withdraw consent where consent is used. Your employer's privacy policy will explain how to exercise these rights.
        </p>
        <p>
          The policy regarding timeframes and any charges associated with these requests will be set out in your employer's privacy policy.
        </p>
        <p>
          While the legal responsibility lies with your employer, FS will assist your employer in handling requests and is happy to address any concerns you may have about your rights and personal data. Please submit your enquiries to info@fabricshift.com.
        </p>

        <h3 id="privacy-cookies">12. Cookies</h3>
        <p>
          The Platform uses cookies (and similar technologies) in order to function properly. A cookie is a small text file created by the websites and apps you visit and may contain information that helps recognise you as a user and remember your preferences. These files are stored on your device and can, among other things, keep you signed in. Information from cookies may be used to improve the user experience and/or the Platform.
        </p>
        <p>
          We use both session cookies (which expire once you close your web browser) and persistent cookies (which stay on your device for a set period of time or until you delete them).
        </p>
        <p>
          Where required by law, we will ask for your consent before setting any non-essential cookies (for example, analytics or performance cookies). Strictly necessary cookies that are required for the Platform to work will be set without consent.
        </p>
        <p>
          You can manage your cookie choices at any time using your browser settings and, where provided, the Platform's cookie controls. You can also delete cookies that have been previously stored. Please refer to your browser's help section for instructions. If you choose not to accept certain cookies, you can still use the Platform, but some functionality may be limited.
        </p>

        <h3 id="privacy-security">13. Data Security</h3>
        <p>
          The security and integrity of your personal data are important to us. FS maintains an information security management approach aligned with ISO/IEC 27001 and uses accepted industry standards, technologies and procedures (including encryption in transit and at rest, access controls, logging and monitoring) to help prevent the loss, misuse or unauthorised disclosure of your personal data. Although we do our best to protect your data, no system is 100% secure. Despite our best efforts, there is always a risk of unauthorised access to your personal data. By using the Platform, you accept this risk.
        </p>
        <p>
          Internal access is segregated between necessary departments within FS and restricted only to personnel who need it, with protections such as multi-factor authentication. We only process personal data under the instructions of your employer, set out in the B2B licence agreement. We have processes to identify data breaches and will inform your employer and the applicable regulator when legally required to do so.
        </p>
        <p>
          FS will set all users up with a username for passwordless login (or your organisation may enable single sign-on (SSO)). If you are having issues logging in, please use the in-app reset option or contact info@fabricshift.com.
        </p>
        <p>
          To minimise the risk of unauthorised access, you should also limit access to your device and/or browser and log out after using the Platform.
        </p>
        <p>
          Note that the privacy policies of external content or websites linked from within the Platform may differ from those of FS and your employer.
        </p>

        <h3 id="privacy-contact">14. Contact Details</h3>
        <p>
          For questions or concerns regarding this Privacy Policy, please contact us at:<br />
          Email: <a href="mailto:info@fabricshift.com">info@fabricshift.com</a><br />
          Address: Edmund House, 12-22 Newhall Street, Birmingham, England, B3 3AS<br />
          Phone: +44 0203 150 0000
        </p>
        <p>
          Our support desk is operational 24/7 and we are always happy to hear from you. For unresolved concerns or legal issues, please contact your employer.
        </p>
      </section>
    </div>
  );
};

export default HabiTrackLegal;